'use client';

import { useFiltersVisibleState } from '@/atoms/search-popup-visible';
import Button from '@/components/shared/Button';
import { useSearchParams } from 'next/navigation';
import SearchSVGWhite from '@/svg/search-white.svg?url';
import SearchInput from '@/components/shared/SearchInput';
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { useFiltersState } from '@/atoms/catalog/filters-state';
import { produce } from 'immer';
import { getProducts } from '@/api/products';
import debounce from 'lodash.debounce';
import { Product } from '@/types';
import Input from '@/components/shared/Input';

export const NavSearch = () => {
    const setFiltersVisible = useFiltersVisibleState();

    const inputRef = useRef<HTMLInputElement>(null);

    const [isPressed, setPressed] = useState(false);
    const router = useRouter();
    const [, setFiltersState] = useFiltersState();

    const searchParams = useSearchParams();
    const searchValue = searchParams.get('search')?.trim();
    const buttonText = searchValue ?? '';

    const [inputWidth, setInputWidth] = useState(0);

    const [searchText, setSearchText] = useState(buttonText);

    const searches =
        typeof window !== 'undefined' ? (JSON.parse(localStorage.getItem('searches') || '[]') as string[] | null) : [];

    const onClick = () => {
        setFiltersVisible(false);
        setPressed(true);
    };

    useEffect(() => {
        if (isPressed && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isPressed]);

    const [products, setProducts] = useState<Product[]>([]);
    const spanRef = useRef<HTMLSpanElement>(null);

    const findProducts = debounce((name: string) => {
        getProducts({
            params: {
                search: name,
                limit: 10,
            },
        }).then((res) => {
            setProducts(res.data?.items || []);
        });
    }, 500);

    useEffect(() => {
        if (spanRef.current) {
            // Устанавливаем ширину input под размер текста или placeholder
            const newWidth = Math.min(spanRef.current.clientWidth, 170);
            setInputWidth(newWidth + 10); // +10 для небольшого отступа
        }
    }, [searchText]);

    const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        const currentTarget = e.currentTarget;

        requestAnimationFrame(() => {
            const active = document.activeElement;
            if (!currentTarget.contains(active)) {
                setPressed(false);
            }
        });
    };

    const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
        findProducts(e.target.value);
    };

    const handleSearch = (search: string) => {
        const { sort, order } = router.query;

        setFiltersState(
            produce((draft) => {
                draft.search = search;
            }),
        );

        setPressed(false);

        router.push(
            {
                pathname: '/search',
                query: {
                    sort: sort ?? 'published_at',
                    order: order ?? 'desc',
                    search,
                },
            },
            undefined,
            { shallow: true },
        );
    };

    return (
        <div
            onBlur={handleBlur}
            className="nav-filter-search-wrapper app-nav__catalog-search-btn"
            style={{ position: 'relative' }}
        >
            <form
                className="nav-filter-search"
                onClick={onClick}
                onSubmit={(event: FormEvent) => {
                    event.preventDefault();
                    const search = searchText.trim();
                    if (!search) return;

                    const prevSearches = JSON.parse(localStorage.getItem('searches') || '[]');
                    const newSearches = [search, ...prevSearches].slice(0, 50);
                    localStorage.setItem('searches', JSON.stringify(newSearches));

                    handleSearch(search);
                }}
            >
                <input
                    style={{ width: inputWidth }}
                    className="m-text-xs"
                    name="q"
                    value={searchText}
                    onChange={handleInput}
                    placeholder="Поиск"
                    ref={inputRef}
                />

                <span
                    ref={spanRef}
                    className="m-text-xs"
                    style={{
                        position: 'absolute',
                        visibility: 'hidden',
                    }}
                >
                    {searchText || 'Поиск'}
                </span>

                {/* // eslint-disable-next-line @next/next/no-img-element */}
                <button>
                    <img
                        src={SearchSVGWhite}
                        alt=""
                        width={20}
                        height={20}
                        className="img-fluid"
                        style={{ width: '20px', height: '20px' }}
                    />
                </button>
            </form>

            {isPressed && (products.length === 0 || !inputRef.current?.value) && searches && searches.length > 0 && (
                <div className="catalog-filter-search-history">
                    {searches.slice(0, 10).map((search, index) => (
                        <Button
                            key={index}
                            className="catalog-filter-search-history-btn"
                            onClick={() => handleSearch(search)}
                        >
                            <div className="text-s" style={{ height: 'fit-content' }}>
                                {search}
                            </div>
                        </Button>
                    ))}
                </div>
            )}
            {isPressed && products.length > 0 && inputRef.current?.value && (
                <div className="catalog-filter-search-history">
                    {products.slice(0, 10).map((product, index) => (
                        <Button
                            key={index}
                            className="catalog-filter-search-history-btn"
                            onClick={() => {
                                const prevSearches = JSON.parse(localStorage.getItem('searches') || '[]');
                                const newSearches = [product.name, ...prevSearches].slice(0, 50);
                                localStorage.setItem('searches', JSON.stringify(newSearches));
                                setPressed(false);

                                router.push(
                                    {
                                        pathname: `/catalog/${product.id}`,
                                    },
                                    undefined,
                                    { shallow: true },
                                );
                            }}
                        >
                            <div className="text-s" style={{ height: 'fit-content' }}>
                                {product.name}
                            </div>
                        </Button>
                    ))}
                </div>
            )}
        </div>
    );
};
